import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { dashboardWallQuery, type DashboardCard } from '../api/dashboard'
import { usePageStyles } from '../ui/usePageStyles'
import { placeLinks } from './cardLinks'

function two(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function pagesOf(cards: DashboardCard[], perPage: number): DashboardCard[][] {
  const pages: DashboardCard[][] = []
  let page: DashboardCard[] = []
  let used = 0
  for (const card of cards) {
    const cells = card.w * card.h || 1
    if (used + cells > perPage && page.length) {
      pages.push(page)
      page = []
      used = 0
    }
    page.push(card)
    used += cells
  }
  if (page.length) pages.push(page)
  return pages
}

/**
 * Wallboard: version-3/dashboard/wall.php + wall.js. Fara meniu, ceas viu,
 * rotire pe 12 celule, reincarcare.
 */
export function DashboardWall({ kind, keyName }: { kind: string; keyName: string }) {
  const search = typeof window !== 'undefined' ? window.location.search : ''
  const wall = useQuery(dashboardWallQuery(kind, keyName, search))
  usePageStyles(['css/app.css', 'css/tokens.css', 'css/od_dashboard.css'], '26.9.6.1')

  if (wall.isPending) return <div className="odb-wall" />
  if (wall.isError || !wall.data) return <div className="odb-wall" />

  return <WallFace wall={wall.data} />
}

function WallFace({
  wall,
}: {
  wall: { title: string; cards: DashboardCard[]; tally: { unreadable: number }; rotate: number; reload: number; perPage: number }
}) {
  const [clock, setClock] = useState('--:--')
  const [page, setPage] = useState(0)
  const [bar, setBar] = useState(0)
  const pages = useMemo(() => pagesOf(wall.cards, wall.perPage), [wall.cards, wall.perPage])
  const rotate = pages.length > 1
  const visible = new Set((pages[page] ?? []).map((c) => c.id))

  useEffect(() => {
    const tick = () => {
      const now = new Date()
      setClock(`${two(now.getHours())}:${two(now.getMinutes())}:${two(now.getSeconds())}`)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    const id = window.setTimeout(() => window.location.reload(), wall.reload * 1000)
    return () => window.clearTimeout(id)
  }, [wall.reload])

  useEffect(() => {
    if (!rotate) return
    let started = Date.now()
    const paint = window.setInterval(() => {
      setBar(Math.min(100, Math.max(0, ((Date.now() - started) / (wall.rotate * 1000)) * 100)))
    }, 200)
    const flip = window.setInterval(() => {
      setPage((n) => (n + 1) % pages.length)
      started = Date.now()
    }, wall.rotate * 1000)
    return () => {
      window.clearInterval(paint)
      window.clearInterval(flip)
    }
  }, [rotate, pages.length, wall.rotate])

  return (
    <div className="odb-wall" data-rotate={wall.rotate} data-reload={wall.reload} data-per-page={wall.perPage}>
      <div className="odb-wall-head">
        <div className="odb-wall-title">{wall.title}</div>
        <div className="odb-wall-meta">
          <span className="odb-wall-clock" id="odbWallClock">
            {clock}
          </span>
        </div>
      </div>
      {wall.cards.length === 0 ? (
        <div className="odb-wall-empty">Nothing on this board is open to you.</div>
      ) : (
        <>
          <div className="odb odb-wall-grid" id="odbWallGrid">
            {wall.cards.map((card) => (
              /* Cardul e copilul DIRECT al locului, ca in `DashboardCardSlot`:
                 `.odb-slot > .odb-card` e regula care il tine in celula grilei,
                 iar un `<div>` pus la mijloc o face sa nu se mai potriveasca. */
              <div
                className={`odb-slot odb-x${card.w} odb-y${card.h}`}
                data-cells={card.w * card.h}
                hidden={rotate && !visible.has(card.id)}
                key={card.id}
                dangerouslySetInnerHTML={{ __html: placeLinks(card.html) }}
              />
            ))}
          </div>
          <div className="odb-wall-progress" hidden={!rotate}>
            <span id="odbWallBar" style={{ width: `${bar}%` }}></span>
          </div>
        </>
      )}
    </div>
  )
}
